/**
 * Header (Compact Single-Bar)
 *
 * Header tunggal yang menggabungkan:
 * - Branding (logo + "Booku")
 * - Info kontekstual (Tahun Buku, Status Buku, Company) — sebelumnya di InfoBar terpisah
 * - User actions (profil, logout)
 *
 * InfoBar terpisah dihapus untuk hemat vertical space.
 *
 * @see .claude/docs/Booku v2/ui-patterns/compact-density-pattern.md
 */

import { Menu, LogOut, User, Calendar, Building2, ChevronDown } from 'lucide-react';
import { Tag } from 'antd';
import { useAuthStore, muatUlangKeHalamanLogin } from '@/modules/auth/authStore';
import { useAppStore } from '@/stores/appStore';
import { useAppTabsStore, createTabFromPath } from '@/stores/appTabsStore';
import { CompanyPopover } from './CompanyPopover';

interface HeaderProps {
  onToggleSidebar: () => void;
}

export function Header({ onToggleSidebar }: HeaderProps) {
  const { user, logout } = useAuthStore();
  const NamaPerusahaan = useAuthStore((state) => state.NamaPerusahaan);
  const { TahunBukuAktif, JenisTahunBuku, StatusBuku } = useAppStore();
  const { openTab } = useAppTabsStore();

  const handleLogout = () => {
    logout();
    muatUlangKeHalamanLogin();
  };

  const handleOpenProfil = () => {
    openTab(createTabFromPath('/profil', 'Profil Saya', <User size={14} />));
  };

  // Warna tag berdasarkan jenis tahun buku
  const jenisColor = JenisTahunBuku === 'NORMAL' ? 'green' : 'orange';
  const statusColor = StatusBuku === 'OPEN' ? 'green' : 'red';

  return (
    <header className="app-header">
      <button className="header-toggle" onClick={onToggleSidebar} title="Toggle Menu">
        <Menu size={16} />
      </button>

      <div className="header-brand">
        <div className="header-logo">B</div>
        <span className="header-title">Booku</span>
      </div>

      <div className="header-spacer" />

      {/* Info Kontekstual: Company → Tahun Buku → Status (urutan kiri ke kanan) */}
      <div className="header-context">
        <CompanyPopover>
          <div className="header-context-item header-context-clickable">
            <Building2 size={13} className="header-context-icon" />
            <span className="header-context-value">
              {NamaPerusahaan || 'Tidak ada perusahaan'}
            </span>
            <ChevronDown size={11} style={{ opacity: 0.7 }} />
          </div>
        </CompanyPopover>

        <div className="header-context-separator" />

        <div className="header-context-item">
          <Calendar size={13} className="header-context-icon" />
          <span className="header-context-label">Tahun Buku:</span>
          <span className="header-context-value">{TahunBukuAktif}</span>
          <Tag color={jenisColor} className="header-context-tag">
            {JenisTahunBuku}
          </Tag>
          <Tag color={statusColor} className="header-context-tag">
            {StatusBuku}
          </Tag>
        </div>
      </div>

      <div className="header-actions">
        {user && (
          <div className="header-user-info">
            <span className="header-user-name">{user.fullName || user.username}</span>
          </div>
        )}

        <button className="header-btn" onClick={handleOpenProfil} title="Profil">
          <User size={16} />
        </button>

        <button className="header-btn" onClick={handleLogout} title="Keluar">
          <LogOut size={16} />
        </button>
      </div>
    </header>
  );
}
